// src/components/Schedule.js

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Box, Typography, Stack, Switch, FormControlLabel,
  InputAdornment, useTheme, Paper, Divider, Fade
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  DateRange as DateRangeIcon,
  AccessTime as AccessTimeIcon,
  Update as UpdateIcon,
  EditCalendar as EditCalendarIcon,
  Save as SaveIcon,
  WbSunny as DayIcon,
  Nightlight as NightIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { format as formatDate } from 'date-fns';

const daysOfWeek = [
  { key: 'L', full: 'Lunedì', value: '1' },
  { key: 'M', full: 'Martedì', value: '2' },
  { key: 'M', full: 'Mercoledì', value: '3' },
  { key: 'G', full: 'Giovedì', value: '4' },
  { key: 'V', full: 'Venerdì', value: '5' },
  { key: 'S', full: 'Sabato', value: '6' },
  { key: 'D', full: 'Domenica', value: '0' },
];

// Componente interno estratto per evitare errori di re-rendering
const SectionBox = ({ title, icon, children }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.background.default, 0.5),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: theme.palette.text.secondary }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
};

export default function Schedule({ open, onClose, onSave, selectedCount, selectedItems = [] }) {
  const theme = useTheme();

  // --- LOGICA ORIGINALE (INVARIATA) ---
  const [scheduleData, setScheduleData] = useState({
    play_date_start: null,
    play_date_end: null,
    play_time_start: null,
    play_time_end: null,
    play_days: ['1', '2', '3', '4', '5', '6', '0'],
    player_time: '',
  });
  const [isAllDay, setIsAllDay] = useState(true);

  useEffect(() => {
    if (selectedCount > 0 && selectedItems[0] === undefined) {
      console.error("Errore: selectedItems non definito correttamente nonostante selectedCount > 0");
      return;
    }
    switch (selectedCount) {
      case 0:
        setScheduleData({
          play_date_start: new Date(),
          play_date_end: null,
          play_time_start: null,
          play_time_end: null,
          play_days: ['1', '2', '3', '4', '5', '6', '0'],
          player_time: '',
        });
        setIsAllDay(true);
        break;
      case 1:
        const item = selectedItems[0];        
        setScheduleData({
          play_date_start: item.play_date_start ? new Date(item.play_date_start) : null,
          play_date_end: item.play_date_end ? new Date(item.play_date_end) : null,
          play_time_start: item.play_time_start ? new Date(`1970-01-01T${item.play_time_start}`) : null,
          play_time_end: item.play_time_end ? new Date(`1970-01-01T${item.play_time_end}`) : null,
          play_days: item.play_days ? item.play_days.split('').map((val, idx) => val === '1' ? daysOfWeek[idx].value : null).filter(Boolean) : ['1', '2', '3', '4', '5', '6', '0'],
          player_time: item.player_time || '',
        });
        if (item.play_time_start === '00:00:00' && item.play_time_end === '23:59:59') {
          setIsAllDay(true);
        } else {
          setIsAllDay(false);
        }
        break;
      default:
        const intersectDates = (field, bound) => {
          const dates = selectedItems.map(item => item[field] ? new Date(item[field]) : null).filter(Boolean);
          if (dates.length === 0) return bound === "lb" ? new Date() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
          const firstDate = dates[0];
          return dates.every(date => date.getTime() === firstDate.getTime()) ? firstDate : (bound === "lb" ? new Date() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))
        };
        const intersectTimes = (field) => {
          const times = selectedItems.map(item => item[field] ? new Date(`1970-01-01T${item[field]}`) : null).filter(Boolean);
          if (times.length === 0) return null;
          const firstTime = times[0];
          return times.every(time => time.getTime() === firstTime.getTime()) ? firstTime : null;
        };
        const intersectDays = () => {
          const allDays = selectedItems.map(item => item.play_days ? item.play_days.split('').map((val, idx) => val === '1' ? daysOfWeek[idx].value : null).filter(Boolean) : []);
          if (allDays.length === 0) return ['1', '2', '3', '4', '5', '6', '0'];
          return daysOfWeek.map(day => allDays.every(days => days.includes(day.value)) ? day.value : null).filter(Boolean);
        };
        setScheduleData({
          play_date_start: intersectDates('play_date_start', 'lb'),
          play_date_end: intersectDates('play_date_end', 'ub'),
          play_time_start: intersectTimes('play_time_start'),
          play_time_end: intersectTimes('play_time_end'),
          play_days: intersectDays(),
          player_time: '',
        });
        const allDay = selectedItems.every(item => item.play_time_start === '00:00:00' && item.play_time_end === '23:59:59');
        setIsAllDay(allDay);
        break;
    }
  }, [selectedCount, selectedItems]);
    
  const handleValueChange = (field, value) => {
    setScheduleData(prev => ({ ...prev, [field]: value }));
  };

  const handleDayToggle = (value) => {
    const currentIndex = scheduleData.play_days.indexOf(value);
    const newDays = [...scheduleData.play_days];

    if (currentIndex === -1) {
      newDays.push(value);
    } else {
      newDays.splice(currentIndex, 1);
    }
    handleValueChange('play_days', newDays);
  };
  
  const toggleAllDay = (event) => {
    const checked = event.target.checked;
    setIsAllDay(checked);
    if (checked) {
      setScheduleData(prev => ({
        ...prev,
        play_time_start: new Date(0, 0, 0, 0, 0, 0),
        play_time_end: new Date(0, 0, 0, 23, 59, 59),
      }));
    } else {
      setScheduleData(prev => ({ ...prev, play_time_start: null, play_time_end: null }));
    }
  };

  const handleSave = async () => {
    const formattedData = {
      play_date_start: scheduleData.play_date_start ? formatDate(scheduleData.play_date_start, 'yyyy-MM-dd') : undefined,
      play_date_end: scheduleData.play_date_end ? formatDate(scheduleData.play_date_end, 'yyyy-MM-dd') : undefined,
      play_time_start: scheduleData.play_time_start ? formatDate(scheduleData.play_time_start, 'HH:mm:ss') : undefined,
      play_time_end: scheduleData.play_time_end ? formatDate(scheduleData.play_time_end, 'HH:mm:ss') : undefined,
      player_time: scheduleData.player_time || undefined,
      play_days: daysOfWeek.map(day => scheduleData.play_days.includes(day.value) ? '1' : '0').join(''),
    };
    Object.keys(formattedData).forEach(key => formattedData[key] === undefined && delete formattedData[key]);
    await onSave(formattedData);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10],
          backdropFilter: 'blur(10px)',
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
        }
      }}
    >
      {/* HEADER */}
      <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                    sx={{ 
                        p: 1.2, 
                        borderRadius: 2, 
                        backgroundColor: alpha(theme.palette.primary.main, 0.1), 
                        color: theme.palette.primary.main 
                    }}
                >
                    <EditCalendarIcon />
                </Box>
                <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        Programmazione
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Modifica {selectedCount} {selectedCount === 1 ? 'elemento selezionato' : 'elementi selezionati'}
                    </Typography>
                </Box>
            </Stack>
            <IconButton onClick={onClose} size="small" sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' } }}>
                <CloseIcon />
            </IconButton>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, pb: 2, pt: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            
            {/* CONTAINER VERTICALE: Ho rimosso Grid e usato Stack per pulizia */}
            <Stack spacing={2}>

              {/* 1. SEZIONE DATE */}
              <SectionBox title="Periodo di Validità" icon={<DateRangeIcon fontSize="small"/>}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <DatePicker
                    label="Data Inizio"
                    value={scheduleData.play_date_start}
                    onChange={(newValue) => handleValueChange('play_date_start', newValue)}
                    slotProps={{ 
                        textField: { 
                            fullWidth: true, 
                            variant: 'outlined',
                            size: 'medium',
                            sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                        } 
                    }}
                  />
                  <DatePicker
                    label="Data Fine"
                    value={scheduleData.play_date_end}
                    onChange={(newValue) => handleValueChange('play_date_end', newValue)}
                    slotProps={{ 
                      textField: { 
                          fullWidth: true, 
                          variant: 'outlined',
                          size: 'medium',
                          sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                      } 
                  }}
                  />
                </Stack>
              </SectionBox>

              {/* 2. SEZIONE ORARI */}
              <SectionBox title="Fascia Oraria" icon={<AccessTimeIcon fontSize="small"/>}>
                 <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.primary" fontWeight={500}>
                          Programmazione giornaliera
                      </Typography>
                      <FormControlLabel 
                          control={<Switch checked={isAllDay} onChange={toggleAllDay} color="primary" />} 
                          label={<Typography variant="body2" fontWeight={600}>{isAllDay ? "Tutto il giorno" : "Personalizzato"}</Typography>}
                          labelPlacement="start"
                          sx={{ ml: 0, mr: 0 }}
                      />
                 </Stack>

                 <Fade in={!isAllDay} unmountOnExit={false}>
                   <Stack direction="row" spacing={2} sx={{ opacity: isAllDay ? 0.4 : 1, pointerEvents: isAllDay ? 'none' : 'auto', transition: 'opacity 0.3s' }}>
                      <TimePicker
                          label="Ora Inizio"
                          value={scheduleData.play_time_start}
                          onChange={(newValue) => handleValueChange('play_time_start', newValue)}
                          slotProps={{ 
                              textField: { 
                                  fullWidth: true, 
                                  InputProps: { startAdornment: (<InputAdornment position="start"><DayIcon fontSize="small" sx={{color: 'orange'}}/></InputAdornment>) },
                                  sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                              } 
                          }}
                      />
                      <TimePicker
                          label="Ora Fine"
                          value={scheduleData.play_time_end}
                          onChange={(newValue) => handleValueChange('play_time_end', newValue)}
                          slotProps={{ 
                              textField: { 
                                  fullWidth: true, 
                                  InputProps: { startAdornment: (<InputAdornment position="start"><NightIcon fontSize="small" sx={{color: 'slateblue'}}/></InputAdornment>) },
                                  sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                              } 
                          }}
                      />
                   </Stack>
                 </Fade>
              </SectionBox>

              {/* 3. SEZIONE GIORNI (CUSTOM UI) */}
              <SectionBox title="Giorni Attivi" icon={<UpdateIcon fontSize="small"/>}>
                 <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                    {daysOfWeek.map((day) => {
                        const isSelected = scheduleData.play_days.includes(day.value);
                        return (
                            <Box 
                              key={day.key} 
                              onClick={() => handleDayToggle(day.value)}
                              sx={{
                                  width: 42,
                                  height: 42,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  backgroundColor: isSelected ? theme.palette.primary.main : alpha(theme.palette.action.active, 0.05),
                                  color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
                                  fontWeight: isSelected ? 700 : 400,
                                  border: isSelected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                                  '&:hover': {
                                      transform: 'scale(1.1)',
                                      backgroundColor: isSelected ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.1),
                                  }
                              }}
                            >
                                {day.key}
                            </Box>
                        )
                    })}
                 </Stack>
                 <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 2 }}>
                     Clicca sui giorni per attivare/disattivare
                 </Typography>
              </SectionBox>

            </Stack>
        </LocalizationProvider>
      </DialogContent>

      <Divider />
      
      <DialogActions sx={{ p: 3, backgroundColor: alpha(theme.palette.background.default, 0.5) }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, px: 3, color: 'text.secondary' }}>
            Annulla
        </Button>
        <Button 
            variant="contained" 
            onClick={() => handleSave().then(() => onClose())}
            startIcon={<SaveIcon />}
            sx={{ 
                borderRadius: 2, 
                px: 4, 
                py: 1,
                boxShadow: `0 8px 16px -4px ${alpha(theme.palette.primary.main, 0.25)}` 
            }}
        >
          Salva Modifiche
        </Button>
      </DialogActions>
    </Dialog>
  );
}